'use client';

import { CameraOff01Icon, Loading03Icon, QrCodeScanIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as React from 'react';

import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { useBarcodeScanner } from './hooks/use-barcode-scanner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './input-otp';
import { cn } from './lib/utils';

const DEFAULT_PIN_LENGTH = 5;
// Allow letters and digits in either case; the value is upper-cased for display
// and submission. `input-otp` rejects characters that don't match this pattern.
const ALPHANUMERIC_PATTERN = '^[A-Za-z0-9]+$';

export interface ScanQrCodeProps {
  /** Called with the decoded value when the camera reads a QR/barcode. */
  onScan: (value: string) => void;
  /** Called with the typed code once it reaches `pinLength` characters. */
  onSubmitPinCode: (code: string) => void;
  /** Length of the manual fallback pin code. Defaults to 5. */
  pinLength?: number;
  /** Disables inputs and shows a spinner while the consumer resolves a value. */
  isResolving?: boolean;
  /** Inline error to show (e.g. "Pin code not found"). */
  error?: string | null;
  /** Controlled open state (supplied by {@link useScanQrCode}). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Override the default icon-button trigger. Pass `null` for no trigger (open it yourself). */
  trigger?: React.ReactNode;
  /** Called when the camera can't be accessed (permission denied, no device, insecure context). */
  onScanError?: (error: unknown) => void;
  /**
   * Remount key for the camera scanner. Change it to re-arm the scanner (it
   * emits once per mount); {@link useScanQrCode} bumps it after a failed scan.
   */
  scanResetKey?: number;
  title?: string;
  description?: string;
  pinHint?: string;
  triggerLabel?: string;
  /** Custom manual fallback, e.g. a worker UUID form. Omit to retain PIN entry. */
  manualEntry?: React.ReactNode;
  cameraUnavailableMessage?: string;
}

/**
 * Camera QR scanner with a manual pin-code fallback, in a dialog. Generic: it
 * only emits the scanned value / entered code — the consumer decides where each
 * points (see `useScanQrCode`). Mirrors artic-hub's `ScanQRCode` widget.
 */
function ScanQrCode({
  onScan,
  onSubmitPinCode,
  pinLength = DEFAULT_PIN_LENGTH,
  isResolving = false,
  error,
  open,
  onOpenChange,
  trigger,
  onScanError,
  scanResetKey,
  title = 'Scan QR code',
  description,
  pinHint = 'Trouble scanning? Enter the work order pin code.',
  triggerLabel = 'Scan QR code',
  manualEntry,
  cameraUnavailableMessage = 'Camera unavailable. Enter the pin code below instead.',
}: ScanQrCodeProps) {
  const defaultTrigger = (
    <Button variant="outline" size="icon" aria-label={triggerLabel}>
      <HugeiconsIcon icon={QrCodeScanIcon} strokeWidth={2} />
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger === undefined ? (
        <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>
      ) : trigger === null ? null : (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      )}
      <DialogContent data-slot="scan-qr-code">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {/* Rendered only while the dialog is open, so the camera is released on
            close. `scanResetKey` remounts it to re-arm after a failed scan. */}
        <ScannerView
          key={scanResetKey}
          onScan={onScan}
          disabled={isResolving}
          cameraUnavailableMessage={cameraUnavailableMessage}
          {...(onScanError ? { onScanError } : {})}
        />

        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-sm">{pinHint}</p>
          {manualEntry === undefined ? (
            <PinCodeInput
              pinLength={pinLength}
              disabled={isResolving}
              onComplete={onSubmitPinCode}
            />
          ) : (
            manualEntry
          )}
          {isResolving ? (
            <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin" strokeWidth={2} />
              Checking…
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ScannerViewProps {
  onScan: (value: string) => void;
  onScanError?: (error: unknown) => void;
  disabled?: boolean;
  cameraUnavailableMessage: string;
}

/** The live camera preview + scanning-window overlay. */
function ScannerView({
  onScan,
  onScanError,
  disabled,
  cameraUnavailableMessage,
}: ScannerViewProps) {
  const [detected, setDetected] = React.useState(false);
  const [cameraError, setCameraError] = React.useState(false);

  const { ref } = useBarcodeScanner({
    onResult: (text) => {
      if (disabled) return;
      setDetected(true);
      onScan(text);
    },
    onError: (err) => {
      setCameraError(true);
      onScanError?.(err);
    },
  });

  if (cameraError) {
    return (
      <div className="bg-muted text-muted-foreground flex flex-col items-center gap-2 rounded-md px-4 py-8 text-center text-sm">
        <HugeiconsIcon icon={CameraOff01Icon} strokeWidth={2} className="size-6" />
        <span>{cameraUnavailableMessage}</span>
      </div>
    );
  }

  return (
    <div className="bg-muted relative aspect-square overflow-hidden rounded-md">
      {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- live camera preview, no captions */}
      <video
        ref={ref}
        className="size-full object-cover"
        autoPlay
        playsInline
        muted
        aria-label="Camera preview"
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 transition-colors',
          detected ? 'bg-scan-guide/70' : 'bg-black/60',
        )}
        style={{
          clipPath:
            'polygon(0% 0%, 0% 100%, 25% 100%, 25% 25%, 75% 25%, 75% 75%, 25% 75%, 25% 100%, 100% 100%, 100% 0%)',
        }}
      />
    </div>
  );
}

interface PinCodeInputProps {
  pinLength: number;
  disabled?: boolean;
  onComplete: (code: string) => void;
}

/**
 * Segmented alphanumeric pin input (shadcn `input-otp`). Upper-cases as you type
 * and fires `onComplete` once every slot is filled. Re-typing after a clear
 * fires it again, so a failed lookup can be retried.
 */
function PinCodeInput({ pinLength, disabled, onComplete }: PinCodeInputProps) {
  const [value, setValue] = React.useState('');

  return (
    <InputOTP
      maxLength={pinLength}
      pattern={ALPHANUMERIC_PATTERN}
      value={value}
      onChange={(next) => {
        setValue(next.toUpperCase());
      }}
      // `onComplete` may arrive before the upper-cased value re-renders, so
      // upper-case here too.
      onComplete={(code) => {
        onComplete(code.toUpperCase());
      }}
      disabled={disabled}
      aria-label="Work order pin code"
      containerClassName="justify-center"
    >
      <InputOTPGroup>
        {Array.from({ length: pinLength }, (_, index) => (
          <InputOTPSlot key={index} index={index} className="size-10 text-base uppercase" />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}

export interface UseScanQrCodeOptions {
  /** Where a scanned value points. Throw to surface an inline error. */
  onScan: (value: string) => void | Promise<void>;
  /** Where an entered pin code points. Throw to surface an inline error. */
  onSubmitPinCode: (code: string) => void | Promise<void>;
  pinLength?: number;
}

export interface ScanQrCodeController {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (value: string) => void;
  onSubmitPinCode: (code: string) => void;
  isResolving: boolean;
  error: string | null;
  pinLength: number;
  scanResetKey: number;
}

/**
 * The "simple hook" for wiring a {@link ScanQrCode} widget: you say where a
 * scan / pin points to, it manages the dialog open state, resolving spinner,
 * error surfacing and auto-close on success. Spread the result onto the widget:
 *
 * ```tsx
 * const scanner = useScanQrCode({
 *   onScan: (id) => navigate({ to: '/quality-packs/$workOrderId', params: { workOrderId: id } }),
 *   onSubmitPinCode: async (code) => { const id = await resolve(code); ... },
 * });
 * return <ScanQrCode {...scanner} />;
 * ```
 */
export function useScanQrCode({
  onScan,
  onSubmitPinCode,
  pinLength = DEFAULT_PIN_LENGTH,
}: UseScanQrCodeOptions): ScanQrCodeController {
  const [open, setOpen] = React.useState(false);
  const [isResolving, setIsResolving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [scanResetKey, setScanResetKey] = React.useState(0);

  // Hold the latest handlers in a ref so the returned callbacks stay stable.
  const handlersRef = React.useRef({ onScan, onSubmitPinCode });
  handlersRef.current = { onScan, onSubmitPinCode };

  const onOpenChange = React.useCallback((next: boolean) => {
    setOpen(next);
    setError(null);
    if (!next) setIsResolving(false);
  }, []);

  // Returns whether the action succeeded, so callers can react to a failure.
  const run = React.useCallback(async (fn: () => void | Promise<void>): Promise<boolean> => {
    setError(null);
    setIsResolving(true);
    try {
      await fn();
      setOpen(false);
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Something went wrong');
      return false;
    } finally {
      setIsResolving(false);
    }
  }, []);

  const handleScan = React.useCallback(
    async (value: string) => {
      const ok = await run(() => handlersRef.current.onScan(value));
      // The camera scanner emits once per mount; if the scan failed (the dialog
      // stays open), remount it so the user can scan again without reopening.
      if (!ok) setScanResetKey((key) => key + 1);
    },
    [run],
  );

  const handleSubmitPinCode = React.useCallback(
    (code: string) => run(() => handlersRef.current.onSubmitPinCode(code)),
    [run],
  );

  return {
    open,
    onOpenChange,
    onScan: handleScan,
    onSubmitPinCode: handleSubmitPinCode,
    isResolving,
    error,
    pinLength,
    scanResetKey,
  };
}

export { ScanQrCode };
